import axios from "axios";
import { disconnectHandler } from "@/utils/disconnectHandler";
import { disconnect } from "@wagmi/core";
import CryptoJS from "crypto-js";
import { config } from "@/wagmi/config";
import * as Sentry from "@sentry/nextjs";
import {
  clearAuth,
  getAuthToken,
  getRefreshToken,
  updateAuthTokens,
} from "./auth";
import { toast } from "react-toastify";

const SECRET_KEY =
  process.env.NEXT_PUBLIC_ENCRYPTION_KEY || "my-super-secret-key";
const ENABLE_ENCRYPTION = process.env.NEXT_PUBLIC_ENABLE_ENCRYPTION === "true";

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleLogout = async () => {
  try {
    clearAuth();
    Sentry.setUser(null);
    if (typeof window !== "undefined" && window.location.pathname !== "/") {
      await disconnect(config);
      disconnectHandler(() => {
        localStorage.removeItem("intercom.intercom-state");
        localStorage.removeItem("wagmi.store");
      });
    }
  } catch {}
};

const decryptForSentry = (encryptedData: string) => {
  try {
    if (!encryptedData) return null;
    const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (error) {
    return "Failed to decrypt payload Sentry";
  }
};

const http = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

http.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (config.url !== "/users/connectWallet") {
      const existingAuth =
        config.headers.Authorization ||
        (config.headers.get ? config.headers.get("Authorization") : undefined);

      if (!existingAuth && token) {
        if (config.headers.set) {
          config.headers.set("Authorization", `Bearer ${token}`);
        } else {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }

    if (ENABLE_ENCRYPTION) {
      (config as unknown as { _rawConfig?: unknown })._rawConfig = {
        url: config.url,
        baseURL: config.baseURL,
        method: config.method,
        data: config.data,
        params: config.params,
      };

      const authHeader =
        config.headers.Authorization ||
        (config.headers.get ? config.headers.get("Authorization") : undefined);

      const realPayload = {
        url: config.url,
        method: config.method,
        data: config.data,
        params: config.params,
        headers: authHeader ? { Authorization: authHeader } : {},
      };

      const encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(realPayload),
        SECRET_KEY,
      ).toString();

      config.baseURL = "";
      config.url = "/api";
      config.method = "POST";
      config.data = { payload: encryptedData };

      if (config.headers) {
        delete config.headers.Authorization;
      }
      if (config.params) {
        delete config.params;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

http.interceptors.response.use(
  (response) => {
    if (ENABLE_ENCRYPTION && response.data && response.data.payload) {
      const bytes = CryptoJS.AES.decrypt(response.data.payload, SECRET_KEY);
      const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
      response.data = decryptedData;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const showErrorToast = originalRequest?.showErrorToast ?? true;

    try {
      let sentryRequestData = originalRequest?.data;
      let sentryResponseData = error.response?.data;
      if (ENABLE_ENCRYPTION) {
        if (originalRequest?.data) {
          try {
            const parsedReqData =
              typeof originalRequest.data === "string"
                ? JSON.parse(originalRequest.data)
                : originalRequest.data;
            if (parsedReqData.payload) {
              sentryRequestData = decryptForSentry(parsedReqData.payload);
            }
          } catch (e) {}
        }
        if (error.response?.data?.payload) {
          sentryResponseData = decryptForSentry(error.response.data.payload);
        }
      }

      Sentry.withScope((scope) => {
        scope.setExtra("Request_Data_Raw", sentryRequestData);
        scope.setExtra("Response_Data_Raw", sentryResponseData);
        scope.setExtra("Response_Status", error.response?.status);
        try {
          scope.setExtra(
            "Request_Data_Stringified",
            JSON.stringify(sentryRequestData, null, 2),
          );
          scope.setExtra(
            "Response_Data_Stringified",
            JSON.stringify(sentryResponseData, null, 2),
          );
        } catch (e) {
          scope.setExtra("Stringify_Error", "Could not stringify payload");
        }
        if (originalRequest?.url) {
          scope.setFingerprint([
            originalRequest.method,
            originalRequest.url,
            String(error.response?.status),
          ]);
        }
        Sentry.captureException(error);
      });
    } catch (sentryError) {
      console.error("Failed to log error to Sentry", sentryError);
    }

    if (error.response?.status === 401) {
      const isAuthRequest =
        originalRequest?.url?.includes("/auth/identityWallet") ||
        originalRequest?.url?.includes("/auth/nonce") ||
        originalRequest?.url?.includes("/auth/verify");
      if (isAuthRequest) {
        return Promise.reject(error);
      }

      const isRefreshRequest = originalRequest?.url?.includes("/auth/refresh");
      if (isRefreshRequest || originalRequest?._retry) {
        await handleLogout();
        return Promise.reject(error);
      }

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        await handleLogout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              if (ENABLE_ENCRYPTION && (originalRequest as any)._rawConfig) {
                const raw = (originalRequest as any)._rawConfig;
                originalRequest.url = raw.url;
                originalRequest.baseURL = raw.baseURL;
                originalRequest.method = raw.method;
                originalRequest.data = raw.data;
                originalRequest.params = raw.params;
              }
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              resolve(http(originalRequest));
            },
            reject: (err: unknown) => {
              reject(err);
            },
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResponse = await axios.post(
          `${process.env.NEXT_PUBLIC_BACKEND_URL}/auth/refresh`,
          { refreshToken },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${refreshToken}`,
            },
          },
        );

        const resData = refreshResponse.data;
        const newAccessToken =
          resData?.data?.token?.accessToken ||
          resData?.token?.accessToken ||
          resData?.data?.accessToken ||
          resData?.accessToken;
        const newRefreshToken =
          resData?.data?.token?.refreshToken ||
          resData?.token?.refreshToken ||
          resData?.data?.refreshToken ||
          resData?.refreshToken ||
          refreshToken;

        if (!newAccessToken) {
          throw new Error("No access token returned from refresh endpoint");
        }

        updateAuthTokens(newAccessToken, newRefreshToken);

        if (ENABLE_ENCRYPTION && (originalRequest as any)._rawConfig) {
          const raw = (originalRequest as any)._rawConfig;
          originalRequest.url = raw.url;
          originalRequest.baseURL = raw.baseURL;
          originalRequest.method = raw.method;
          originalRequest.data = raw.data;
          originalRequest.params = raw.params;
        }

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        processQueue(null, newAccessToken);
        return http(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        await handleLogout();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    if (showErrorToast && error.response) {
      if (error.response.data?.message !== "User not found in the binary tree") {
        toast.error(error.response.data?.message || "An error occurred");
      }
    }

    return Promise.reject(error);
  },
);

export default http;
